/*
  Выбор счетов в реестре для действий полосы под таблицей (16.10.2026).

  Отметки живут дольше страницы: человек листает реестр и набирает счета с
  нескольких страниц, поэтому выбор хранится по счёту, а не по строке таблицы.
  Вместе с идентификатором запоминаются номер и адрес - окна подтверждения
  называют счета ими, а строки с прошлых страниц на экране уже нет.

  28.10.2026 (аудит, раздел 5.3): выбор принадлежит ОТБОРУ. Сменился фильтр или поиск -
  выбор снимается и об этом сказано: иначе «Выбрать весь отбор» оставлял счета прежнего
  отбора, и действие уходило на них, хотя на экране их уже нет. Листание страниц и смена
  порядка отбор не меняют, и отметки при них остаются.
*/

import { useCallback, useMemo, useState } from 'react'

// Всё, что не определяет состав отбора: страница, порядок и размер страницы.
const NOT_FILTER = ['offset', 'sort', 'direction', 'limit']

function selectionKey(params) {
  return JSON.stringify(
    [...params.entries()].filter(([name]) => !NOT_FILTER.includes(name)).sort(),
  )
}

export default function useRegistryPick(params) {
  const [chosen, setChosen] = useState(() => new Map())
  // Сколько отметок снято сменой отбора; 0 - ничего не снималось (или человек уже выбрал заново).
  const [cleared, setCleared] = useState(0)
  const [seen, setSeen] = useState(() => selectionKey(params))

  // Сброс при отрисовке, а не в эффекте: иначе один кадр действия ушли бы на старый выбор.
  const key = selectionKey(params)
  if (key !== seen) {
    setSeen(key)
    setCleared(chosen.size)
    if (chosen.size > 0) setChosen(new Map())
  }

  const toggle = useCallback((row) => {
    setCleared(0)
    setChosen((current) => {
      const next = new Map(current)
      if (next.has(row.account_id)) next.delete(row.account_id)
      else next.set(row.account_id, { account_id: row.account_id, number: row.number })
      return next
    })
  }, [])

  const toggleAll = useCallback((rows, on) => {
    setCleared(0)
    setChosen((current) => {
      const next = new Map(current)
      for (const row of rows) {
        if (on) next.set(row.account_id, { account_id: row.account_id, number: row.number })
        else next.delete(row.account_id)
      }
      return next
    })
  }, [])

  // Снять отметки целиком или только с перечисленных - тех, что действие
  // обработало. Отказанные остаются отмеченными: человек видит, что с ними
  // ещё предстоит решить.
  const drop = useCallback((ids) => {
    setChosen((current) => {
      if (!ids) return new Map()
      const next = new Map(current)
      for (const id of ids) next.delete(id)
      return next
    })
  }, [])

  const list = useMemo(() => [...chosen.values()], [chosen])

  return { chosen, list, cleared, toggle, toggleAll, drop }
}
